/**
 * @lodex-pro/web-admin/features/catalog-sync/hooks — Task 25.5 (DEC-116).
 *
 * Sync config/runs/trigger (new — /v1/platform/catalog/sync-*) + the change-queue
 * review flow (reuses the Task 24.9 endpoints /v1/platform/catalog/change-queue).
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  apiErrorFromResponse,
  authedFetch,
  type EmptyBody,
  useApiMutation,
  useApiOpts,
  useApiQuery,
} from '@lodex-pro/web-shared';
import type { WebApiOpts } from '@lodex-pro/web-shared';
import type { CatalogChangeDisplay } from '@lodex-pro/shared';

// ─── Sync config ─────────────────────────────────────────────────────────────

/** One source file of a run (catalog_sync_runs.source_files; RRC plan PR 3). */
export interface SourceFileStatus {
  readonly name: string;
  readonly host?: string;
  readonly outcome: 'DOWNLOADED' | 'UNCHANGED' | 'FAILED' | 'OVERRIDE';
  readonly httpStatus?: number | null;
  readonly attempts?: number;
  readonly error?: string | null;
}

/** An agency's newest run, as GET /sync-config reports it. */
export interface LastSyncRun {
  readonly status: string | null;
  /** DOWNLOAD_FAILED | SOURCE_PAGE_CHANGED | PARSE_FAILED | DB_FAILED, when FAILED. */
  readonly errorKind: string | null;
  readonly failedFile: string | null;
  readonly sourceHost: string | null;
  readonly httpStatus: number | null;
  readonly downloadAttempts: number | null;
  readonly errorMessage: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly sourceFiles: readonly SourceFileStatus[];
}

export interface SyncConfig {
  readonly id: string;
  readonly stateAgency: string;
  readonly syncIntervalDays: number;
  readonly isActive: boolean;
  readonly lastSyncAt: string | null;
  readonly lastSyncStatus: string | null;
  readonly lastSyncRecordsAdded: number | null;
  readonly lastSyncChangesFlagged: number | null;
  readonly lastSyncError: string | null;
  readonly nextSyncAt: string | null;
  /** Corbin: "show when the file fails to download". Absent from an older API. */
  readonly lastRun?: LastSyncRun | null;
  /** When the agency's source files were last fetched successfully. */
  readonly lastSuccessfulDownloadAt?: string | null;
}

export const SYNC_CONFIG_KEY = ['platform', 'catalog', 'sync-config'] as const;
export const SYNC_RUNS_KEY = ['platform', 'catalog', 'sync-runs'] as const;

export function useSyncConfigs(): ReturnType<
  typeof useApiQuery<{ configs: readonly SyncConfig[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ configs: readonly SyncConfig[] }>(opts, '/v1/platform/catalog/sync-config', {
    queryKey: SYNC_CONFIG_KEY,
    staleTime: 15_000,
  });
}

export interface UpdateSyncConfigBody {
  syncIntervalDays: number;
  isActive: boolean;
}

export function useUpdateSyncConfig(
  agency: string,
): ReturnType<typeof useApiMutation<UpdateSyncConfigBody, { config: SyncConfig }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateSyncConfigBody, { config: SyncConfig }>(
    opts,
    `/v1/platform/catalog/sync-config/${agency}`,
    'PATCH',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: SYNC_CONFIG_KEY }) },
  );
}

export interface TriggerResult {
  triggered: boolean;
  taskArn?: string | null;
  message: string;
}

export function useTriggerSync(
  agency: string,
): ReturnType<typeof useApiMutation<EmptyBody, TriggerResult>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<EmptyBody, TriggerResult>(
    opts,
    `/v1/platform/catalog/sync/${agency}/trigger`,
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: SYNC_RUNS_KEY }) },
  );
}

// ─── Sync runs ───────────────────────────────────────────────────────────────

export interface SyncRun {
  readonly id: string;
  readonly stateAgency: string;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly status: string | null;
  readonly recordsProcessed: number | null;
  readonly recordsAdded: number | null;
  readonly recordsUpdated: number | null;
  readonly changesFlagged: number | null;
  readonly errorMessage: string | null;
  readonly logGroup: string | null;
  readonly errorKind?: string | null;
  readonly failedFile?: string | null;
  readonly httpStatus?: number | null;
  readonly downloadAttempts?: number | null;
  readonly sourceFiles?: readonly SourceFileStatus[] | null;
}

/** Run History shows this many when first expanded, and "Show more" adds this many. */
export const RUNS_STEP = 10;
/** The API's `limit` ceiling (catalog-sync.ts runsQuery). */
export const RUNS_MAX_LIMIT = 100;

type SyncRunsPage = { runs: readonly SyncRun[]; total: number; limit: number; offset: number };

/** The newest `limit` runs. Not fetched until `enabled` (Run History starts collapsed). */
export function useSyncRuns(
  limit: number,
  enabled = true,
): ReturnType<typeof useApiQuery<SyncRunsPage>> {
  const opts = useApiOpts();
  const qs = new URLSearchParams({ limit: String(limit), offset: '0' }).toString();
  return useApiQuery<SyncRunsPage>(opts, `/v1/platform/catalog/sync-runs?${qs}`, {
    queryKey: [...SYNC_RUNS_KEY, qs],
    staleTime: 10_000,
    enabled,
  });
}

/** The newest run, for the collapsed Run History header. */
export function useLatestSyncRun(): ReturnType<typeof useApiQuery<SyncRunsPage>> {
  return useSyncRuns(1);
}

/**
 * How many runs FAILED since `startedAfter`. Only `total` is used, so one row is enough.
 * `startedAfter` must be stable across renders: it is part of the query key.
 */
export function useFailedSyncRunCount(
  startedAfter: string,
): ReturnType<typeof useApiQuery<SyncRunsPage>> {
  const opts = useApiOpts();
  const qs = new URLSearchParams({ status: 'FAILED', startedAfter, limit: '1' }).toString();
  return useApiQuery<SyncRunsPage>(opts, `/v1/platform/catalog/sync-runs?${qs}`, {
    queryKey: [...SYNC_RUNS_KEY, qs],
    staleTime: 10_000,
  });
}

// ─── Change-queue review (reuses Task 24.9 endpoints) ────────────────────────

export interface PlatformChange {
  readonly id: string;
  readonly stateAgency: string;
  readonly changeType: string;
  readonly wellId: string | null;
  readonly oldValues: Record<string, unknown>;
  readonly newValues: Record<string, unknown>;
  readonly detectedAt: string;
  readonly reviewStatus: string;
  readonly reviewNotes: string | null;
  readonly paelodeApprovedAt: string | null;
  readonly notifyHaulers: boolean;
  readonly wellName: string | null;
  readonly apiNumber: string | null;
  readonly operatorName: string | null;
  readonly affectedHaulerCount: number;
  /** Operator companies with a tank, route or battery on the well (0262). */
  readonly operatorCount: number;
  /** Jobs still open that pick up from the well (0262). */
  readonly openJobCount: number;
  /** The change in plain words, built by the API (ids resolved to names). */
  readonly display: CatalogChangeDisplay;
}

/**
 * The detail's change: a list row without affectedHaulerCount. The detail sends the
 * haulers themselves (count = affectedHaulers.length), so reading the count off this
 * object is a type error, not a blank page.
 */
export type PlatformChangeDetail = Omit<PlatformChange, 'affectedHaulerCount'>;

export interface AffectedHauler {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly batteryCount: number;
  readonly batteryNames: string | null;
  /** Active pickup-catalog rows for the well (0 or 1). */
  readonly pickupCount: number;
  /** Active disposal authorizations for the well (0 or 1). */
  readonly disposalCount: number;
  readonly reviewStatus: string | null;
}

export type PlatformReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'DEFERRED';

export const CHANGE_QUEUE_KEY = ['platform-change-queue'] as const;

export function useChangeQueue(
  reviewStatus?: PlatformReviewStatus,
  stateAgency?: string,
  changeType?: string,
): ReturnType<typeof useApiQuery<{ changes: PlatformChange[] }>> {
  const opts = useApiOpts();
  const params = new URLSearchParams();
  if (reviewStatus !== undefined) params.set('reviewStatus', reviewStatus);
  if (stateAgency !== undefined && stateAgency !== '') params.set('stateAgency', stateAgency);
  if (changeType !== undefined && changeType !== '') params.set('changeType', changeType);
  const qs = params.toString();
  return useApiQuery<{ changes: PlatformChange[] }>(
    opts,
    `/v1/platform/catalog/change-queue${qs === '' ? '' : `?${qs}`}`,
    { queryKey: [...CHANGE_QUEUE_KEY, qs], staleTime: 10_000 },
  );
}

export function useChangeDetail(
  id: string | null,
): ReturnType<
  typeof useApiQuery<{ change: PlatformChangeDetail; affectedHaulers: AffectedHauler[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ change: PlatformChangeDetail; affectedHaulers: AffectedHauler[] }>(
    opts,
    `/v1/platform/catalog/change-queue/${id ?? ''}`,
    { queryKey: ['platform-change-detail', id ?? ''], enabled: id !== null, staleTime: 5_000 },
  );
}

export type AdminAction = 'approve' | 'reject' | 'defer';

async function patchAction(opts: WebApiOpts, path: string, notes?: string): Promise<void> {
  const res = await authedFetch(opts, path, {
    method: 'PATCH',
    body: JSON.stringify(notes !== undefined && notes !== '' ? { notes } : {}),
  });
  if (!res.ok) throw await apiErrorFromResponse(res, path);
}

export type BulkSkipReason =
  | 'not_found'
  | 'not_pending'
  | 'type_excluded'
  | 'has_haulers'
  | 'open_jobs'
  | 'operator_links'
  | 'error';

export interface BulkApproveResult {
  readonly batchId: string;
  readonly approved: number;
  readonly skipped: ReadonlyArray<{ id: string; reason: BulkSkipReason }>;
}

/** POST …/bulk-approve with the exact ids the admin saw; the server re-checks each one. */
export function useBulkApproveChanges(): UseMutationResult<
  BulkApproveResult,
  Error,
  { ids: string[] }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids }) => {
      const path = '/v1/platform/catalog/change-queue/bulk-approve';
      const res = await authedFetch(opts, path, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw await apiErrorFromResponse(res, path);
      return (await res.json()) as BulkApproveResult;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CHANGE_QUEUE_KEY });
      void qc.invalidateQueries({ queryKey: ['platform-change-detail'] });
    },
  });
}

export function useChangeAction(): UseMutationResult<
  void,
  Error,
  { id: string; action: AdminAction; notes?: string }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, notes }) =>
      patchAction(opts, `/v1/platform/catalog/change-queue/${id}/${action}`, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHANGE_QUEUE_KEY });
      void qc.invalidateQueries({ queryKey: ['platform-change-detail'] });
    },
  });
}
